<template>
  <!-- eslint-disable -->
  <div class="container-fluid">
    <div class="row header">
      <div class="col-lg-10 offset-lg-1 col-xl-8 offset-xl-2">
        <p class="dg-header-label">Brand</p>
        <h1 class="dg-header">Colors</h1>
        <p>Colors have strong ties to psychology, namely affecting emotion or feeling. Colors can represent caution or failure, success or progress, and serve to visually cue the user to perform (or not perform) particular actions. Further, color serves to create a unified user interface.</p>
      </div>
    </div>

    <div class="row content">
      <div class="col-lg-10 offset-lg-1 col-xl-8 offset-xl-2">
      <!-- Content -->
        <div id="colorSample">
          <template>
            <div v-for="(section, index) in colorSections" :key="index">
              <div class="row">
                <div class="col">
                  <p class="h3 dg-subheader">{{section.title}}</p>
                  <p>{{section.description}}</p>
                </div>
              </div>
              <div class="row mt-3 mb-5">
                <div v-for="(color, index) in section.colors" :key="index" class="col-6 col-sm-4 col-xl-3 mb-4">
                  <div class="dg-color-sample">
                    <div class="dg-sample" v-bind:class="color.class"></div>
                    <div class="dg-sample-desc">
                      <p class="dg-sample-label">{{color.label}}</p>
                      <p class="dg-sample-info"><b>Hex:</b> {{color.hex}}</p>
                      <p class="dg-sample-info"><b>RGB:</b> {{color.rgb}}</p>
                      <p class="dg-sample-info code">{{color.var}}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <div class="row mt-4">
            <div class="col">
              <p class="h3 dg-subheader">Gradients</p>
              <p>Gradients should be used for backgrounds where appropriate.</p>
            </div>
          </div>
          <div class="row mt-4">
            <div class="col-6 col-md-4 col-xl-3 mb-4">
              <div class="dg-color-sample">
                <div class="dg-sample bg-gradient"></div>
                <div class="dg-sample-desc">
                  <p class="dg-sample-label">Brand Gradient</p>
                  <p class="dg-sample-info">Primary - Secondary</p>
                </div>
              </div>
            </div>
          </div>
          <div class="row mt-4">
            <div class="col">
              <p class="h3 dg-subheader">Accessibility</p>
              WCAG (Web Content Accessibility Guidelines) ensure that content is accessible by everyone, regardless of disability or user device. To meet these standards, text and interactive elements should have a color contrast ratio of at least 4.5:1. This ensures that viewers who cannot see the full color spectrum are able to read the text.</p>
            <p>In order to ensure a proper color contrast ratio, you can utilize webAIMS' <a href="https://webaim.org/resources/contrastchecker/">Color Contraster Checker</a>.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- eslint-enable -->
</template>

<script>
export default {
  name: "Colors",
  data() {
    return {
      colorSections: [
        {
          title: "Brand Colors",
          description:
            "Primary colors can be utilized for link colors, backgrounds and buttons. Remember to refer to the accessibility guidelines below to ensure that your colors are ADA compliant with text.",
          colors: [
            {
              label: "Primary",
              class: "bg-primary",
              hex: "#28807C",
              rgb: "40, 128, 124",
              var: "$primary"
            },
            {
              label: "Secondary",
              class: "bg-secondary",
              hex: "#0077A7",
              rgb: "0, 119, 167",
              var: "$secondary"
            }
          ]
        },
        {
          title: "UI Colors",
          description:
            "In addition to the Brand Colors, UI Colors can be used for elements including backgrounds, borders and tertiary items.",
          colors: [
            {
              label: "Black",
              class: "bg-black",
              hex: "#000000",
              rgb: "0, 0 ,0",
              var: "$black"
            },
            {
              label: "White",
              class: "bg-white",
              hex: "#FFFFFF",
              rgb: "255, 255, 255",
              var: "$white"
            },
            {
              label: "Dark Gray",
              class: "bg-dark-gray",
              hex: "#323e43",
              rgb: "50, 62, 67",
              var: "$dark-gray"
            },
            {
              label: "Medium Gray",
              class: "bg-medium-gray",
              hex: "#8ea6af",
              rgb: "142, 166, 175",
              var: "$medium-gray"
            },
            {
              label: "Light Gray",
              class: "bg-light-gray",
              hex: "#EAEAEA",
              rgb: "234, 234, 234",
              var: "$light-gray"
            },
            {
              label: "90% Gray",
              class: "bg-gray-900",
              hex: "#1d2528",
              rgb: "29, 37, 40",
              var: "$gray-900"
            },
            {
              label: "80% Gray",
              class: "bg-gray-800",
              hex: "#323E43",
              rgb: "50, 62, 67",
              var: "$gray-800"
            },
            {
              label: "70% Gray",
              class: "bg-gray-700",
              hex: "#51646D",
              rgb: "81, 100, 109",
              var: "$gray-700"
            },
            {
              label: "60% Gray",
              class: "bg-gray-600",
              hex: "#728A92",
              rgb: "114, 138, 146",
              var: "$gray-600"
            },
            {
              label: "50% Gray",
              class: "bg-gray-500",
              hex: "#8EA6AF",
              rgb: "142, 166, 175",
              var: "$gray-500"
            },
            {
              label: "40% Gray",
              class: "bg-gray-400",
              hex: "#C4CED1",
              rgb: "196, 206, 209",
              var: "$gray-400"
            },
            {
              label: "30% Gray",
              class: "bg-gray-300",
              hex: "#E3E3E3",
              rgb: "227, 227, 227",
              var: "$gray-300"
            },
            {
              label: "20% Gray",
              class: "bg-gray-200",
              hex: "#EAEAEA",
              rgb: "234, 234, 234",
              var: "$gray-200"
            },
            {
              label: "10% Gray",
              class: "bg-gray-100",
              hex: "#F7F7F7",
              rgb: "247, 247, 247",
              var: "$gray-100"
            },
            {
              label: "Light Teal",
              class: "bg-light-teal",
              hex: "#31A09D",
              rgb: "49, 160, 157",
              var: "$light-teal"
            },
            {
              label: "Light Blue",
              class: "bg-light-blue",
              hex: "#008EC6",
              rgb: "0, 142, 198",
              var: "$light-blue"
            }
          ]
        },
        {
          title: "Support Colors",
          description: "Support colors should be used for alerts.",
          colors: [
            {
              label: "Danger",
              class: "bg-danger",
              hex: "#B80000",
              rgb: "184, 0, 0",
              var: "$danger"
            },
            {
              label: "Warning",
              class: "bg-warning",
              hex: "#C75300",
              rgb: "199, 83, 0",
              var: "$warning"
            },
            {
              label: "Success",
              class: "bg-success",
              hex: "#158655",
              rgb: "21, 134, 85",
              var: "$success"
            }
          ]
        }
      ]
    };
  }
};
</script>

/* esling-disable */
<style lang="sass?outputStyle=expanded">
.dg-color-sample{
  .dg-sample{
    width: 3.75rem;
    height: 3.75rem;
    display: block;
    border-radius: 50%;
    box-shadow: 0 1px 9px 0 rgba(0,0,0,0.13);
    margin-bottom: .5rem;
  }
  .dg-sample-desc{
    display: block;
    .dg-sample-label{
      margin-bottom: .25rem;
      font-weight: 700;
      font-size: .875rem;
    }
    .dg-sample-info{
      margin-bottom: .25rem;
      font-size: .8rem;
      text-transform: uppercase;
      &.code{
        text-transform: none;
        background: transparent;
      }
    }
  }
}
</style>
/* eslint-enable */



// WEBPACK FOOTER //
// src/components/routes/Colors.vue